import { cloneDeep, camelCase } from "lodash-es";
import { isRef, computed, watch } from "vue";
import gql from "fraql";
/**
 * Modify formField array to combine e.g name formField and nameTranslation formField into one
 * as entity FieldTranslation
 * @param {*} formFields
 * @return formFields array
 */
export function combineTranslationField(formFields) {
    formFields = cloneDeep(formFields !== null && formFields !== void 0 ? formFields : []);
    const fieldsTranslatable = [];
    // find all FieldTranslation entities
    formFields.forEach(formField => {
        if (formField.entity != "FieldTranslation")
            return;
        fieldsTranslatable.push(formField.name);
    });
    // replace e.g descriptionTranslation formField's name to just description and store original value
    // in translationField so AppTranslateInputs can get translated values
    fieldsTranslatable.forEach(formFieldName => {
        var _a;
        const originalFieldName = formFieldName.split("Translations")[0];
        const originalField = (_a = formFields.find(x => x.name == originalFieldName)) !== null && _a !== void 0 ? _a : {
            inputType: "input"
        };
        formFields = formFields.filter(x => x.name != originalFieldName);
        const translationFieldIndex = formFields.findIndex(x => x.name == formFieldName);
        formFields[translationFieldIndex].name = originalFieldName;
        formFields[translationFieldIndex].translationFieldName = formFieldName;
        switch (originalField.inputType) {
            case "textarea":
                formFields[translationFieldIndex].translationFieldComponentName =
                    "VTextarea";
                break;
            case "richtextarea":
                formFields[translationFieldIndex].translationFieldComponentName =
                    "AppRichTextField";
                break;
            case "text":
            case "input":
            default:
                formFields[translationFieldIndex].translationFieldComponentName =
                    "VTextField";
        }
    });
    return formFields;
}
export function createMutationArguments(initForm, ignoreFields = []) {
    const { formFields = [], readOnlyFields = [] } = initForm;
    const { args, variables } = formFields.reduce((obj, formField) => {
        if (readOnlyFields.includes(formField.name) ||
            ignoreFields.includes(formField.name) ||
            formField.parentName // ignore fields that are under another field
        ) {
            return obj;
        }
        let dataType = formField.dataType;
        switch (dataType) {
            case "Integer":
                dataType = "Int";
                break;
        }
        obj.variables.push(`$${camelCase(formField.name)}: ${dataType}`);
        obj.args.push(`${camelCase(formField.name)}: $${camelCase(formField.name)}`);
        return obj;
    }, { args: [], variables: [] });
    return {
        args: args.join(`\n`),
        variables: variables.join(`\n`)
    };
}
/**
 * Create pair values for customFieldValues based on formFields with entity  CustomFieldValue and values from form
 * @param form
 * @param formFields
 * @returns array with pairs of customFieldDefinitionId and its value
 */
export function createCustomFieldValuePairs(form, formFields) {
    return formFields
        .filter(x => x.entity == "CustomFieldValue")
        .reduce((f, customF) => {
        const value = form[customF.name];
        if (value == undefined)
            return f;
        f.push({
            customFieldDefinitionId: customF.name,
            value
        });
        return f;
    }, []);
}
/**
 * Mutates form to include property of customFieldDefinitionId and its value
 * If validationObject is given and field is dirty, then by that times field exists and prevent
 * overwritting it by
 * @param form form where to set properties to
 * @param customFieldValues array of object's customFieldValues array
 * @param validationObject v$.form object
 * @returns nothing
 */
export function addCustomFieldValuesToForm(form, customFieldValues, validationObject) {
    cloneDeep(customFieldValues).forEach(customFieldValue => {
        var _a;
        if ((_a = validationObject === null || validationObject === void 0 ? void 0 : validationObject[customFieldValue.customFieldDefinitionId]) === null || _a === void 0 ? void 0 : _a.$dirty) {
            return;
        }
        if (isRef(form)) {
            form.value[customFieldValue.customFieldDefinitionId] =
                customFieldValue.value;
        }
        else {
            form[customFieldValue.customFieldDefinitionId] = customFieldValue.value;
        }
    });
}
export const createMutationArgumentsFragment = gql `
  fragment _ on FormField {
    name
    dataType
  }
`;
export function useFormFieldDisplayCondition(formFields, formValue) {
    function localIsFieldVisible(field) {
        return isFieldVisible(formValue.value, field);
    }
    const trackVisibilities = computed(() => {
        return formFields.value
            .filter(f => localIsFieldVisible(f))
            .map(x => x.name);
    });
    watch(trackVisibilities, (newVisibles = [], oVisibles = []) => {
        const fieldsThatDissapeared = [];
        oVisibles.forEach(fieldName => {
            if (newVisibles.includes(fieldName))
                return;
            fieldsThatDissapeared.push(fieldName);
        });
        fieldsThatDissapeared.forEach(fieldName => {
            let valSetter = null;
            if (Array.isArray(formValue.value[fieldName]))
                valSetter = [];
            formValue.value[fieldName] = valSetter;
        });
    });
    return {
        isFieldVisible: localIsFieldVisible
    };
}
export function isFieldVisible(modelValue, field) {
    // No visibleWhen means always visible
    if (!field.visibleWhen) {
        return true;
    }
    const conditions = Array.isArray(field.visibleWhen)
        ? field.visibleWhen
        : [field.visibleWhen];
    // All conditions must be true (AND logic)
    return conditions.every(condition => {
        const fieldValue = modelValue[condition.field];
        return evaluateCondition(condition.condition, fieldValue, condition.value || condition.values);
    });
}
function evaluateCondition(condition, fieldValue, expectedValue) {
    switch (condition) {
        case "hasAnyValue":
            return (fieldValue && (Array.isArray(fieldValue) ? fieldValue.length > 0 : true));
        case "hasNoValue":
            return (fieldValue == null ||
                fieldValue == undefined ||
                fieldValue == "" ||
                (Array.isArray(fieldValue) && fieldValue.length === 0));
        case "equals":
            return fieldValue === expectedValue;
        case "notEquals":
            return fieldValue !== expectedValue;
        case "in":
            return Array.isArray(expectedValue) && expectedValue.includes(fieldValue);
        case "notIn":
            return (Array.isArray(expectedValue) && !expectedValue.includes(fieldValue));
        case "greaterThan":
            return Number(fieldValue) > Number(expectedValue);
        case "contains":
            return String(fieldValue).includes(expectedValue);
        default:
            console.warn(`Unknown visibility condition: ${condition}`);
            return true;
    }
}
